import { z } from "zod";
import { PATIENT_GROUPS } from "./patient-group";
import { svRequiredLocalizedTextSchema } from "./intake-template";
import { journeyStepKeySchema } from "./journey-steps";

// TES-408 — Drive/admin-owned copy for the "Dina steg" surface. The app's
// locale files remain the FALLBACK for any step without a published row, so
// content migrates to Drive step by step and a missing row never blanks the
// ladder. Authored copy must carry Swedish (TES-361); English falls back
// sv-ward at render (localizedOr).

export const JOURNEY_STEP_COPY_STATUSES = ["draft", "published"] as const;
export const journeyStepCopyStatusSchema = z.enum(JOURNEY_STEP_COPY_STATUSES);

// Create/update payload (admin + bulk import). `stepKey` is the identity —
// one row per (tenant, step).
// TES-554 — a structured information block shown on a step (today: the
// "Viktig information inför din provtagning" on `labs`, canonical in the
// CP-06 document). Structured, not free text, so the client can render the
// items as a list; server-resolved so the text is an import, not a release.
export const stepInfoItemSchema = z.object({
  label: svRequiredLocalizedTextSchema,
  text: svRequiredLocalizedTextSchema,
});
export const stepInfoBlockSchema = z.object({
  title: svRequiredLocalizedTextSchema,
  intro: svRequiredLocalizedTextSchema,
  items: z.array(stepInfoItemSchema).min(1).max(12),
});
export type StepInfoBlock = z.infer<typeof stepInfoBlockSchema>;

export const journeyStepCopyUpsertSchema = z.object({
  stepKey: journeyStepKeySchema,
  title: svRequiredLocalizedTextSchema,
  body: svRequiredLocalizedTextSchema,
  // Only steps with a failure state (e.g. intake hard-stop) carry attention
  // copy; only patient-action steps carry a CTA label.
  attention: svRequiredLocalizedTextSchema.nullable().optional(),
  // The "När detta är klart" line.
  unlocks: svRequiredLocalizedTextSchema,
  cta: svRequiredLocalizedTextSchema.nullable().optional(),
  info: stepInfoBlockSchema.nullable().optional(),
  status: journeyStepCopyStatusSchema.default("draft"),
});
export type JourneyStepCopyUpsert = z.infer<typeof journeyStepCopyUpsertSchema>;

// Admin read DTO — the full stored row for one step.
export const journeyStepCopyDtoSchema = journeyStepCopyUpsertSchema.extend({
  id: z.string().uuid(),
});
export type JourneyStepCopyDto = z.infer<typeof journeyStepCopyDtoSchema>;

// The patient-facing resolved shape: locale-resolved strings for ONE step,
// served in /me next to `journeySteps`. Only PUBLISHED rows appear; the app
// falls back to its bundled locale strings per missing key.
// TES-794 (Ö-178) — which patient variant a copy row is written for.
//
// Emelie 2026-09-18: the F-01 rung must not promise a consultation to a
// prescription-transfer patient, because her ladder has no booking rung. The
// row said, to everyone:
// "Du får din första återkoppling och kan, om Ella verkar relevant för dig, boka ditt vårdmöte."
// One rung later her journey says "Du behöver inte boka något vårdmöte." The
// two contradicted each other
// a rung apart.
//
// `all` is the shared row every step has today and stays the default, so
// adding this changes nothing until a variant row is authored. A variant row
// wins over the shared one for that patient group, and only for that step.
//
// Not nullable on purpose: Postgres treats NULLs as distinct in a unique
// index, so a nullable variant would silently allow two "shared" rows for the
// same step — which is exactly the ambiguity this is meant to remove.
export const JOURNEY_STEP_COPY_VARIANTS = ["all", ...PATIENT_GROUPS] as const;
export const journeyStepCopyVariantSchema = z.enum(JOURNEY_STEP_COPY_VARIANTS);
export type JourneyStepCopyVariant = z.infer<
  typeof journeyStepCopyVariantSchema
>;

export const resolvedStepInfoSchema = z.object({
  title: z.string(),
  intro: z.string(),
  items: z.array(z.object({ label: z.string(), text: z.string() })),
});
export type ResolvedStepInfo = z.infer<typeof resolvedStepInfoSchema>;

export const resolvedStepCopySchema = z.object({
  title: z.string(),
  body: z.string(),
  attention: z.string().nullable(),
  unlocks: z.string(),
  cta: z.string().nullable(),
  // TES-554: null when the step has no information block.
  info: resolvedStepInfoSchema.nullable(),
  // TES-1127 — the line under a rung the doctor ruled out ("Ej aktuellt").
  // Optional: app builds before 2.0.6 ignore it and render a skipped rung
  // as a title only.
  skippedBody: z.string().nullable().optional(),
  // TES-1150 — a second, quieter action on the step: "Kontakta Ella
  // Support" on "Aktivera abonnemanget". Optional: app builds before 2.0.7
  // ignore it (the help line in `info` names Ella Support for them).
  secondaryCta: z.string().nullable().optional(),
});
export type ResolvedStepCopy = z.infer<typeof resolvedStepCopySchema>;

// TES-1127 (Emelie 2026-10-06) — "Blodprov vid behov" when no new blood test
// was ordered: "Ej aktuellt" with this line, no CTA and no sampling
// information. Swedish verbatim; English ours.
export const LABS_NOT_NEEDED_STEP_BODY: Record<"sv" | "en", string> = {
  sv: "Läkaren bedömer att du inte behöver lämna något nytt blodprov just nu.",
  en: "The doctor's assessment is that you do not need to give a new blood test right now.",
};

// TES-1127 (Emelie 2026-10-06) — the "Behandlingsbeslut" rung while the
// meeting is held and no decision is published: the waiting status, verbatim.
// The title is the step's existing one. English ours.
export const DECISION_PENDING_STEP: {
  title: Record<"sv" | "en", string>;
  body: Record<"sv" | "en", string>;
} = {
  title: { sv: "Behandlingsbeslut", en: "Treatment decision" },
  body: {
    sv: "Vårdmöte genomfört – läkaren slutför bedömningen",
    en: "Consultation held – the doctor is completing the assessment",
  },
};

// TES-1150 (Emelie 2026-10-07, after the first patient) — the
// "Aktivera abonnemanget" rung, from a published positive decision until the
// subscription is verified. Her copy, verbatim (English ours). It replaces
// TES-1133's (the Ö-319 support text, which said the doctor would finish the
// prescription after the payment: since TES-1150 he finishes it before).
//
//   - body: her two sentences, the second with her VERIFIED address masked
//     ("Exempel: m•••@gmail.com", maskEmail below). Server-resolved per
//     patient, so every installed app build shows it.
//   - info: the status "Väntar på aktivering" and the help line, as the
//     step's information block, which every build since 2.0.4 draws on any
//     rung that carries one. No "När detta är klart" line: what follows the
//     step is on the ladder itself.
//   - cta: "Skicka informationen igen", never "Skicka betalningslänken igen".
//     It only re-sends the transactional e-mail (subscription-link.service:
//     no browser, no checkout, no link in the app, no status change). App
//     2.0.6 draws the button from this label; 2.0.4/2.0.5 draw none.
//   - secondaryCta: "Kontakta Ella" (app 2.0.7).
//   - TES-1278 (TES-1266): "Ella Support" in `help` and `secondaryCta` is
//     now "Ella", the name the patient sees ("Ella Support" stays the
//     internal name of the function). Every build has "Kontakta Ella", so
//     there is no gate.
//   - resent / recentlySent: the result lines after a re-send, returned by
//     the re-send itself so 2.0.7 shows her words (2.0.6 has its own).
export const SUBSCRIPTION_STEP: {
  title: Record<"sv" | "en", string>;
  body: Record<"sv" | "en", (maskedEmail: string | null) => string>;
  status: Record<"sv" | "en", string>;
  help: Record<"sv" | "en", string>;
  cta: Record<"sv" | "en", string>;
  secondaryCta: Record<"sv" | "en", string>;
  resent: Record<"sv" | "en", string>;
  recentlySent: Record<"sv" | "en", string>;
} = {
  title: { sv: "Aktivera abonnemanget", en: "Activate your subscription" },
  body: {
    sv: (maskedEmail) =>
      "Du är godkänd för behandling hos Ella. För att behandlingen ska kunna starta behöver ditt abonnemang aktiveras.\n\n" +
      (maskedEmail
        ? `Information om hur du gör har skickats till ${maskedEmail}.`
        : "Information om hur du gör har skickats till din e-postadress."),
    en: (maskedEmail) =>
      "You have been approved for treatment with Ella. Your subscription needs to be activated before your treatment can start.\n\n" +
      (maskedEmail
        ? `Information on how to do it has been sent to ${maskedEmail}.`
        : "Information on how to do it has been sent to your e-mail address."),
  },
  status: { sv: "Väntar på aktivering", en: "Waiting for activation" },
  help: {
    sv: "Hittar du inte mejlet? Kontrollera skräpposten eller kontakta Ella.",
    en: "Can't find the e-mail? Check your spam folder or contact Ella.",
  },
  cta: { sv: "Skicka informationen igen", en: "Send the information again" },
  secondaryCta: { sv: "Kontakta Ella", en: "Contact Ella" },
  resent: {
    sv: "Informationen har skickats igen. Kontrollera även skräpposten.",
    en: "The information has been sent again. Please also check your spam folder.",
  },
  recentlySent: {
    sv: "Informationen skickades nyss. Kontrollera din inkorg och skräpposten.",
    en: "The information was just sent. Please check your inbox and spam folder.",
  },
};

// TES-1150 — "Exempel: m•••@gmail.com": the first character of the local
// part, three dots, the whole domain. Null for anything that is not an
// address, so the step falls back to "din e-postadress".
export function maskEmail(email: string | null | undefined): string | null {
  if (!email) return null;
  const at = email.lastIndexOf("@");
  if (at < 1 || at === email.length - 1) return null;
  return `${email[0]}•••${email.slice(at)}`;
}

// TES-1150 (Emelie 2026-10-07) — "Hämta ut läkemedlet", once the verified
// subscription has released a new prescription: "Vid ett nytt recept visas:
// Receptet finns att hämta ut eller den beslutade uthämtningsuppgiften:
// Hämta ut läkemedlet". Her words, verbatim (English ours). The check-off
// "Jag har hämtat ut läkemedlet" stays on Hem's CP-12 card.
export const MEDICATION_PICKUP_STEP: {
  title: Record<"sv" | "en", string>;
  body: Record<"sv" | "en", string>;
} = {
  title: { sv: "Hämta ut läkemedlet", en: "Collect your medicine" },
  body: {
    sv: "Receptet finns att hämta ut.",
    en: "Your prescription is ready to be collected.",
  },
};

// TES-1150 — the start rung of a patient who paid before the doctor had
// finished the prescription step (an interrupted session, or a payment made
// under the old order). Nothing about a prescription and nothing that says
// the treatment is ready: the second half of TES-1069 point 9's waiting line.
export const START_NOT_RELEASED_BODY: Record<"sv" | "en", string> = {
  sv: "Du får ett besked här när allt är klart.",
  en: "You will hear from us here when everything is ready.",
};

// TES-1159 (Emelie 2026-10-07, point 6) — the start step of a released
// patient who has not registered her first dose: "Behandlingsstart" (or
// "Fortsatt behandling" on the transfer ladder) is the current step and
// "Registrera din första dos" the one remaining task. The CTA label is
// Emelie's, verbatim; the body is OURS, proposed for her review (English
// ours). No "När detta är klart" line: the next step, "Första veckan i
// behandling", is listed right below it.
export const FIRST_DOSE_START_STEP: {
  body: Record<"sv" | "en", string>;
  cta: Record<"sv" | "en", string>;
} = {
  body: {
    sv: "Registrera din första dos när du har tagit den. Din första vecka i behandling räknas från det datumet.",
    en: "Register your first dose once you have taken it. Your first week of treatment counts from that date.",
  },
  cta: { sv: "Registrera din första dos", en: "Register your first dose" },
};

export const journeyStepsCopyMapSchema = z.record(
  journeyStepKeySchema,
  resolvedStepCopySchema,
);
export type JourneyStepsCopyMap = z.infer<typeof journeyStepsCopyMapSchema>;
